// Flags for the language menu.
//
// A flag stands for a country, not a language, so this is a deliberate
// shorthand: each language gets the flag its speakers most readily recognise
// (English -> the UK, Irish -> Ireland). The native name always sits beside the
// flag, so the flag is a visual cue and never the only label.
//
// Inline SVG rather than emoji: Windows renders flag emoji as bare letter pairs
// ("GB"), which is exactly the untidy look this menu is meant to replace.
//
// Pure and dependency-free so it can be unit-tested without a DOM.

export type FlagCountry = "gb" | "de" | "es" | "fr" | "ie" | "it" | "nl" | "pt";

const LANGUAGE_TO_COUNTRY: Record<string, FlagCountry> = {
  en: "gb",
  de: "de",
  es: "es",
  fr: "fr",
  ga: "ie",
  it: "it",
  nl: "nl",
  pt: "pt",
};

// Which flag to show for a locale code. Regional forms ("pt-BR", "de_AT") fall
// back to their base language; a language we have no flag for returns null so
// the caller can show a neutral icon instead of a wrong flag.
export const flagCountryFor = (code: unknown): FlagCountry | null => {
  if (typeof code !== "string" || code.length === 0) return null;

  const normalised = code.toLowerCase();
  const base = normalised.split(/[-_]/)[0];

  return LANGUAGE_TO_COUNTRY[normalised] ?? LANGUAGE_TO_COUNTRY[base] ?? null;
};

// Every flag is drawn on a 3:2 canvas so they line up in the menu. The UK's
// real 2:1 proportions are cropped to fit, which is how flag icons usually
// handle it and is invisible at 20px.
const triband = (a: string, b: string, c: string, vertical: boolean): string =>
  vertical
    ? `<rect width="1" height="2" fill="${a}"/><rect x="1" width="1" height="2" fill="${b}"/><rect x="2" width="1" height="2" fill="${c}"/>`
    : `<rect width="3" height="0.667" fill="${a}"/><rect y="0.667" width="3" height="0.667" fill="${b}"/><rect y="1.333" width="3" height="0.667" fill="${c}"/>`;

const FLAG_BODIES: Record<FlagCountry, string> = {
  gb:
    '<rect width="3" height="2" fill="#012169"/>' +
    '<path d="M0,0L3,2M3,0L0,2" stroke="#fff" stroke-width="0.4"/>' +
    '<path d="M0,0L3,2M3,0L0,2" stroke="#C8102E" stroke-width="0.14"/>' +
    '<path d="M1.5,0V2M0,1H3" stroke="#fff" stroke-width="0.6"/>' +
    '<path d="M1.5,0V2M0,1H3" stroke="#C8102E" stroke-width="0.36"/>',
  de: triband("#000", "#DD0000", "#FFCE00", false),
  es: '<rect width="3" height="2" fill="#AA151B"/><rect y="0.5" width="3" height="1" fill="#F1BF00"/>',
  fr: triband("#002395", "#fff", "#ED2939", true),
  ie: triband("#169B62", "#fff", "#FF883E", true),
  it: triband("#009246", "#fff", "#CE2B37", true),
  nl: triband("#AE1C28", "#fff", "#21468B", false),
  pt:
    '<rect width="3" height="2" fill="#FF0000"/><rect width="1.2" height="2" fill="#006600"/>' +
    '<circle cx="1.2" cy="1" r="0.36" fill="#FFCC00"/><circle cx="1.2" cy="1" r="0.22" fill="#FF0000"/>',
};

export const flagSvg = (country: FlagCountry): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">${FLAG_BODIES[country]}</svg>`;
